import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../services/api'
import { toast } from 'react-toastify'

interface Category {
  id: string
  name: string
}

interface ArticleData {
  title: string
  content: string
  excerpt: string
  categoryId: string
  tags: string[]
  isPublished: boolean
}

export const ArticleEditorPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = id && id !== 'new'

  const [loading, setLoading] = useState(isEdit ? true : false)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [error, setError] = useState('')

  const [articleData, setArticleData] = useState<ArticleData>({
    title: '',
    content: '',
    excerpt: '',
    categoryId: '',
    tags: [],
    isPublished: false
  })

  const [tagInput, setTagInput] = useState('')

  useEffect(() => {
    fetchCategories()
    if (isEdit) {
      fetchArticle()
    }
  }, [isEdit, id])

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/articles/categories/list')
      setCategories(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const fetchArticle = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/admin/articles/${id}`)
      const article = response.data.data
      setArticleData({
        title: article.title,
        content: article.content,
        excerpt: article.excerpt || '',
        categoryId: article.category?.id || '',
        tags: article.tags || [],
        isPublished: article.isPublished
      })
    } catch (error) {
      toast.error('Failed to load article')
      navigate('/admin/articles')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (publish: boolean = false) => {
    setError('')

    if (!articleData.title.trim()) {
      setError('Title is required')
      return
    }

    if (!articleData.content.trim()) {
      setError('Content is required')
      return
    }

    try {
      setSaving(true)
      const data = {
        ...articleData,
        isPublished: publish
      }

      if (isEdit) {
        await api.patch(`/admin/articles/${id}`, data)
        toast.success('Article updated successfully')
      } else {
        await api.post('/admin/articles', data)
        toast.success('Article created successfully')
      }

      navigate('/admin/articles')
    } catch (error: any) {
      setError(error.response?.data?.error?.message || 'Failed to save article')
      toast.error('Failed to save article')
    } finally {
      setSaving(false)
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !articleData.tags.includes(tagInput.trim())) {
      setArticleData({
        ...articleData,
        tags: [...articleData.tags, tagInput.trim()]
      })
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setArticleData({
      ...articleData,
      tags: articleData.tags.filter(t => t !== tag)
    })
  }

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-3">Loading article...</p>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="fas fa-edit me-2 text-primary"></i>
          {isEdit ? 'Edit Article' : 'Create New Article'}
        </h2>
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/admin/articles')}
        >
          <i className="fas fa-times me-2"></i>
          Cancel
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Title *</Form.Label>
                  <Form.Control
                    type="text"
                    size="lg"
                    placeholder="Enter article title"
                    value={articleData.title}
                    onChange={(e) => setArticleData({ ...articleData, title: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Excerpt (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Brief summary of the article (shown in article list)"
                    value={articleData.excerpt}
                    onChange={(e) => setArticleData({ ...articleData, excerpt: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Content *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={15}
                    placeholder="Write your article content here..."
                    value={articleData.content}
                    onChange={(e) => setArticleData({ ...articleData, content: e.target.value })}
                    style={{ fontFamily: 'monospace' }}
                  />
                  <Form.Text className="text-muted">
                    Tip: Use line breaks to format your content. HTML tags are supported.
                  </Form.Text>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">Article Settings</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={articleData.categoryId}
                  onChange={(e) => setArticleData({ ...articleData, categoryId: e.target.value })}
                >
                  <option value="">No Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tags</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Add a tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddTag()
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={handleAddTag}
                  >
                    Add
                  </Button>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {articleData.tags.map((tag, index) => (
                    <span key={index} className="badge bg-secondary">
                      {tag}
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: '0.6em' }}
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </span>
                  ))}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="publish-switch"
                  label="Published"
                  checked={articleData.isPublished}
                  onChange={(e) => setArticleData({ ...articleData, isPublished: e.target.checked })}
                />
                <Form.Text className="text-muted">
                  Publish this article to make it visible to users
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="border-0 bg-light">
            <Card.Body>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => handleSave(articleData.isPublished)}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      {isEdit ? 'Update Article' : 'Create Article'}
                    </>
                  )}
                </Button>
                
                {!articleData.isPublished && (
                  <Button
                    variant="success"
                    onClick={() => handleSave(true)}
                    disabled={saving}
                  >
                    <i className="fas fa-check me-2"></i>
                    Save & Publish
                  </Button>
                )}

                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/admin/articles')}
                  disabled={saving}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}







